import { useCallback, useState } from 'react';

/*
 * WHERE YOU HAVE BEEN ON THE MAP, so there is a way back.
 *
 * Every proper noun in a verse is a door, which makes the map a delight to wander and a maze to
 * leave: you follow Abraham to Isaac to Ishmael to Hagar and cannot retrace a step. This is the
 * trail — a stack of the nodes visited, the current one on top. Selecting a new node pushes it,
 * `back` pops to the one before, and closing (selecting nothing) clears the trail so a fresh open
 * does not resume an old wander. Pure functions, so the whole of it is tested without a component.
 */

/** Push a newly selected node onto the trail. `null` clears it (the panel closed). */
export function pushSelection(history: string[], id: string | null): string[] {
  if (id === null) return [];
  if (history[history.length - 1] === id) return history; // already here — a re-select is not a step
  return [...history, id];
}

/** Step back to the previous node, dropping the current one. */
export function popSelection(history: string[]): string[] {
  return history.slice(0, -1);
}

/** The node in view — the top of the trail, or nothing. */
export function currentSelection(history: string[]): string | null {
  return history.length > 0 ? history[history.length - 1]! : null;
}

export interface SelectionHistory {
  /** The node currently open. */
  selected: string | null;
  /** Whether there is a previous node to step back to. */
  canGoBack: boolean;
  /** Open a node (push), or close everything with `null`. */
  navigate: (id: string | null) => void;
  /** Step back to the previous node. */
  back: () => void;
}

export function useSelectionHistory(): SelectionHistory {
  const [history, setHistory] = useState<string[]>([]);
  const navigate = useCallback((id: string | null) => setHistory(current => pushSelection(current, id)), []);
  const back = useCallback(() => setHistory(current => popSelection(current)), []);
  return {
    selected: currentSelection(history),
    canGoBack: history.length > 1,
    navigate,
    back,
  };
}
